import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import VideocamIcon from "@mui/icons-material/Videocam";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";
import { useSnackbar } from "notistack";
import {
  fetchExitBlockingCameras,
  fetchExitBlockingPolygon,
  saveExitBlockingPolygon,
  fetchExitBlockingSnapshot,
} from "../../services/exitBlockingApi";

import {
  polygonToPercent,
  percentToPolygon,
  validatePolygon,
  parseResolution,
  formatPointsLabel,
} from "../../utils/polygonUtils";

const POINT_COUNT_OPTIONS = [3, 4, 5, 6, 8, 10, 12];

const DEFAULT_REF_WIDTH = 1920;
const DEFAULT_REF_HEIGHT = 1080;

const StatusChip = ({ status, isStreaming }) => {
  const label = isStreaming ? "Streaming" : status || "Unknown";
  const color = isStreaming ? "success" : status === "active" ? "info" : "default";
  return <Chip label={label} color={color} size="small" variant="outlined" />;
};

const CameraDetails = ({ cam }) => {
  if (!cam) return null;
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1} mt={1}>
      {cam.building && (
        <Typography variant="caption" color="text.secondary">
          <strong>Building:</strong> {cam.building}
        </Typography>
      )}
      {cam.floor_level && (
        <Typography variant="caption" color="text.secondary">
          <strong>Floor:</strong> {cam.floor_level}
        </Typography>
      )}
      {cam.location && (
        <Typography variant="caption" color="text.secondary">
          <strong>Location:</strong> {cam.location}
        </Typography>
      )}
      {cam.zone_count !== undefined && (
        <Typography variant="caption" color="text.secondary">
          <strong>Zones:</strong> {cam.active_zone_count ?? cam.zone_count}
        </Typography>
      )}
      {cam.health?.resolution && (
        <Typography variant="caption" color="text.secondary">
          <strong>Resolution:</strong> {cam.health.resolution}
        </Typography>
      )}
      <StatusChip status={cam.status} isStreaming={cam.is_streaming} />
    </Stack>
  );
};

// The editor is shared: another feature (e.g. Lost & Found) passes its own API calls,
// camera filter and wording; the defaults are Exit Blocking's.
const EXIT_BLOCKING_API = {
  fetchCameras: fetchExitBlockingCameras,
  fetchPolygon: fetchExitBlockingPolygon,
  savePolygon: saveExitBlockingPolygon,
  fetchSnapshot: fetchExitBlockingSnapshot,
};

const isExitBlockingCamera = (cam) =>
  cam.is_blocked_exit_camera !== undefined
    ? cam.is_blocked_exit_camera === true
    : cam.status === "active" || cam.features?.includes("exit_blocking");

const EXIT_BLOCKING_TEXT = {
  title: "Exit Blocking — Edit Polygon Area of Interest",
  description:
    "Define emergency exit doorways, pathways, and fire evacuation routes. Select an eligible camera, choose vertex count, and draw on the canvas.",
  cameraLabel: "Select Exit Blocking Camera",
  noCameras: "No cameras with Exit Blocking monitoring activated were found.",
  pickCamera:
    "Select an eligible camera from the dropdown to start drawing or editing its exit blocking polygon area.",
  canvasLabel: "Exit Pathway Canvas",
  loadError: "Unable to load exit-blocking cameras. Check backend connection.",
};

const ExitBlockingPolygonEditor = ({
  api = EXIT_BLOCKING_API,
  isEligible = isExitBlockingCamera,
  text: textOverrides,
  onSaved,
} = {}) => {
  const text = { ...EXIT_BLOCKING_TEXT, ...textOverrides };
  const { token } = useRecoilValue(authState);
  const { enqueueSnackbar } = useSnackbar();

  // Camera list state
  const [cameras, setCameras] = useState([]);
  const [loadingCameras, setLoadingCameras] = useState(true);
  const [cameraError, setCameraError] = useState(null);

  // Selected camera + polygon state
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [loadingPolygon, setLoadingPolygon] = useState(false);
  const [polygonError, setPolygonError] = useState(null);

  // Drawing state (percent space, 0..100)
  const [points, setPoints] = useState([]);
  const [savedPoints, setSavedPoints] = useState([]);
  const [targetPointCount, setTargetPointCount] = useState(4);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [isActive, setIsActive] = useState(true);

  // Ref dimensions
  const [refWidth, setRefWidth] = useState(DEFAULT_REF_WIDTH);
  const [refHeight, setRefHeight] = useState(DEFAULT_REF_HEIGHT);

  // Save state
  const [saving, setSaving] = useState(false);

  // Snapshot (camera frame used as canvas background)
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const svgRef = useRef(null);

  // Load camera list
  useEffect(() => {
    let cancelled = false;

    const loadCameras = async () => {
      setLoadingCameras(true);
      setCameraError(null);
      try {
        const data = await api.fetchCameras(token);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        // Filter cameras eligible for this feature
        const eligible = list.filter(isEligible);
        setCameras(eligible);
        if (eligible.length === 0) {
          setCameraError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching exit-blocking cameras:", err);
          setCameraError(
            err?.response?.data?.message ||
              err?.response?.data?.detail ||
              text.loadError
          );
        }
      } finally {
        if (!cancelled) setLoadingCameras(false);
      }
    };

    if (token) {
      loadCameras();
    } else {
      setLoadingCameras(false);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load polygon when camera selection changes
  const loadPolygon = useCallback(
    async (streamId) => {
      if (!streamId) return;

      setLoadingPolygon(true);
      setPolygonError(null);
      setPoints([]);
      setSavedPoints([]);
      setIsActive(true);
      // Clear stale snapshot
      setSnapshotUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });

      const cam = cameras.find((c) => c.stream_id === streamId || String(c.id) === String(streamId));
      if (cam?.health?.resolution) {
        const { width, height } = parseResolution(cam.health.resolution);
        setRefWidth(width);
        setRefHeight(height);
      } else {
        setRefWidth(DEFAULT_REF_WIDTH);
        setRefHeight(DEFAULT_REF_HEIGHT);
      }

      // Fetch snapshot in the background (non-blocking)
      setSnapshotLoading(true);
      api.fetchSnapshot(streamId, token)
        .then((url) => setSnapshotUrl(url))
        .catch((err) => {
          console.warn("Could not load camera snapshot:", err);
          setSnapshotUrl(null);
        })
        .finally(() => setSnapshotLoading(false));

      try {
        const data = await api.fetchPolygon(streamId, token);

        const rw = data.ref_width > 0 ? data.ref_width : refWidth;
        const rh = data.ref_height > 0 ? data.ref_height : refHeight;
        setRefWidth(rw);
        setRefHeight(rh);
        setIsActive(data.is_active !== false);

        if (Array.isArray(data.polygon) && data.polygon.length > 0) {
          const pct = polygonToPercent(data.polygon, rw, rh);
          setPoints(pct);
          setSavedPoints(pct);
          const cnt = data.polygon.length;
          if (POINT_COUNT_OPTIONS.includes(cnt)) {
            setTargetPointCount(cnt);
          } else {
            setTargetPointCount(cnt > 0 ? cnt : 4);
          }
        } else {
          setPoints([]);
          setSavedPoints([]);
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          setPoints([]);
          setSavedPoints([]);
          setPolygonError(null);
        } else {
          console.error("Error fetching exit-blocking polygon:", err);
          setPolygonError(
            err?.response?.data?.message ||
              err?.response?.data?.detail ||
              "Failed to load existing polygon. You can draw a new one."
          );
          setPoints([]);
          setSavedPoints([]);
        }
      } finally {
        setLoadingPolygon(false);
      }
    },
    [token, cameras, refWidth, refHeight, api]
  );

  const handleCameraChange = (e) => {
    const id = e.target.value;
    setSelectedCameraId(id);
    setPolygonError(null);
    setPoints([]);
    setSavedPoints([]);
    if (!id) {
      setSnapshotUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
    if (id) {
      loadPolygon(id);
    }
  };


  const [hoverCoords, setHoverCoords] = useState(null);

  const getSvgCoords = (e) => {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    return {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    };
  };

  const handleSvgClick = (e) => {
    if (draggingIndex !== null) return;
    if (!selectedCameraId || loadingPolygon) return;
    if (points.length >= targetPointCount) return;

    const coords = getSvgCoords(e);
    if (!coords) return;
    setPoints((prev) => [...prev, coords]);
  };

  const handlePointMouseDown = (index, e) => {
    e.stopPropagation();
    setDraggingIndex(index);
  };

  const handleSvgMouseMove = (e) => {
    if (!svgRef.current) return;
    const coords = getSvgCoords(e);
    if (!coords) return;

    if (draggingIndex !== null) {
      setPoints((prev) =>
        prev.map((pt, i) => (i === draggingIndex ? coords : pt))
      );
    } else {
      setHoverCoords(coords);
    }
  };

  const handleSvgMouseUp = () => {
    setDraggingIndex(null);
  };

  const handleSvgMouseLeave = () => {
    setDraggingIndex(null);
    setHoverCoords(null);
  };

  const handleUndo = () => setPoints((prev) => prev.slice(0, -1));
  const handleClear = () => setPoints([]);
  const handleResetToSaved = () => setPoints(savedPoints);

  const handleSave = async () => {
    if (!selectedCameraId) {
      enqueueSnackbar("Please select a camera first.", { variant: "warning" });
      return;
    }

    const { valid, message } = validatePolygon(points, targetPointCount);
    if (!valid) {
      enqueueSnackbar(message, { variant: "error" });
      return;
    }

    const polygon = percentToPolygon(points, refWidth, refHeight);

    setSaving(true);
    try {
      const response = await api.savePolygon(
        selectedCameraId,
        {
          polygon,
          ref_width: refWidth,
          ref_height: refHeight,
          is_active: isActive,
        },
        token
      );

      if (response?.polygon && Array.isArray(response.polygon)) {
        const rw = response.ref_width > 0 ? response.ref_width : refWidth;
        const rh = response.ref_height > 0 ? response.ref_height : refHeight;
        const updatedPct = polygonToPercent(response.polygon, rw, rh);
        setPoints(updatedPct);
        setSavedPoints(updatedPct);
        setRefWidth(rw);
        setRefHeight(rh);
        if (response.is_active !== undefined) setIsActive(response.is_active);
      } else {
        setSavedPoints(points);
      }

      const camName =
        cameras.find(
          (c) => c.stream_id === selectedCameraId || String(c.id) === String(selectedCameraId)
        )?.name || "camera";
      enqueueSnackbar(
        `Area of Interest saved successfully for ${camName}!`,
        { variant: "success" }
      );
      if (onSaved) onSaved(selectedCameraId, response);
    } catch (err) {
      console.error("Error saving exit-blocking polygon:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to save polygon. Please try again.";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const selectedCamera =
    cameras.find(
      (c) => c.stream_id === selectedCameraId || String(c.id) === String(selectedCameraId)
    ) || null;
  const isPolygonComplete = points.length === targetPointCount;
  const hasUnsavedChanges =
    JSON.stringify(points) !== JSON.stringify(savedPoints);

  const { valid: isSaveValid } = validatePolygon(points, targetPointCount);
  const canSave = isSaveValid && !saving && selectedCameraId;

  const svgPointsStr = points.map((p) => `${p.x}%,${p.y}%`).join(" ");

  return (
    <Card sx={{ p: 3, bgcolor: "background.paper" }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {text.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {text.description}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems="flex-start"
      >
        <Box flex={1} minWidth={0}>
          <FormControl fullWidth size="small">
            <InputLabel id="eb-camera-select-label">
              {loadingCameras ? "Loading cameras…" : text.cameraLabel}
            </InputLabel>
            <Select
              labelId="eb-camera-select-label"
              value={selectedCameraId}
              label={
                loadingCameras ? "Loading cameras…" : text.cameraLabel
              }
              onChange={handleCameraChange}
              disabled={loadingCameras || !!cameraError}
            >
              {cameras.map((cam) => {
                const id = cam.stream_id || cam.id;
                return (
                  <MenuItem key={id} value={id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <VideocamIcon fontSize="small" color="action" />
                      <span>
                        {cam.camera_name}
                        {cam.building ? ` — ${cam.building}` : ""}
                        {cam.floor_level ? `, Floor ${cam.floor_level}` : ""}
                      </span>
                    </Stack>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {selectedCamera && <CameraDetails cam={selectedCamera} />}
        </Box>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="eb-point-count-label">Number of Points</InputLabel>
          <Select
            labelId="eb-point-count-label"
            value={targetPointCount}
            label="Number of Points"
            onChange={(e) => {
              setTargetPointCount(Number(e.target.value));
              setPoints([]);
            }}
            disabled={!selectedCameraId || loadingPolygon}
          >
            {POINT_COUNT_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n} Points
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Chip
          label={formatPointsLabel(points.length, targetPointCount)}
          color={isPolygonComplete ? "success" : points.length > 0 ? "warning" : "default"}
          variant="outlined"
          icon={isPolygonComplete ? <CheckCircleOutlineIcon /> : undefined}
          sx={{ alignSelf: "center" }}
        />
      </Stack>

      {cameraError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cameraError}
        </Alert>
      )}
      {polygonError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {polygonError}
        </Alert>
      )}

      {/* {selectedCameraId && !loadingPolygon && (
        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              Zone Active{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                (toggle to enable/disable exit obstruction detection on save)
              </Typography>
            </Typography>
          }
          sx={{ mb: 2 }}
        />
      )} */}

      {loadingCameras ? (
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      ) : cameras.length === 0 && !cameraError ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: "center", bgcolor: "background.default", borderRadius: 2 }}
        >
          <BlockIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            {text.noCameras}
          </Typography>
        </Paper>
      ) : !selectedCameraId ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: "center", bgcolor: "background.default", borderRadius: 2 }}
        >
          <Typography variant="body1" color="text.secondary">
            {text.pickCamera}
          </Typography>
        </Paper>
      ) : loadingPolygon ? (
        <Box display="flex" justifyContent="center" py={10}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading existing polygon…
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Box>
          {!isPolygonComplete && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {points.length === 0
                ? `Click inside the canvas to place ${targetPointCount} vertices.`
                : `${targetPointCount - points.length} more ${
                    targetPointCount - points.length === 1 ? "point" : "points"
                  } needed to complete the polygon.`}
            </Alert>
          )}

          <Paper
            elevation={2}
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: 860,
              mx: "auto",
              bgcolor: "#0d0d0d",
              borderRadius: 2,
              overflow: "hidden",
              userSelect: "none",
              aspectRatio: `${refWidth} / ${refHeight}`,
            }}
          >
            {/* Camera snapshot background */}
            {snapshotUrl ? (
              <Box
                component="img"
                src={snapshotUrl}
                alt={`${selectedCamera?.name || "Camera"} snapshot`}
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                  display: "block",
                }}
              />
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "repeating-linear-gradient(45deg, #1a1a1a, #1a1a1a 10px, #222 10px, #222 20px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {snapshotLoading ? (
                  <CircularProgress size={32} sx={{ color: "rgba(255,255,255,0.4)" }} />
                ) : (
                  <Typography variant="body2" color="rgba(255,255,255,0.25)">
                    {selectedCamera?.name || "Camera"} — {text.canvasLabel}
                  </Typography>
                )}
              </Box>
            )}


            <svg
              ref={svgRef}
              data-testid="exit-blocking-svg"
              onClick={handleSvgClick}
              onMouseMove={handleSvgMouseMove}
              onMouseUp={handleSvgMouseUp}
              onMouseLeave={handleSvgMouseLeave}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                cursor: points.length >= targetPointCount ? "default" : "crosshair",
              }}
            >
              {/* Dynamic live guide line from last placed point to current mouse position */}
              {!isPolygonComplete && points.length > 0 && hoverCoords && draggingIndex === null && (
                <line
                  x1={`${points[points.length - 1].x}%`}
                  y1={`${points[points.length - 1].y}%`}
                  x2={`${hoverCoords.x}%`}
                  y2={`${hoverCoords.y}%`}
                  stroke="#ffb74d"
                  strokeWidth="2.5"
                  strokeDasharray="6 4"
                  strokeOpacity="0.85"
                />
              )}

              {/* Polygon fill & complete boundary when polygon reaches target count */}
              {isPolygonComplete && (
                <polygon
                  points={svgPointsStr}
                  fill="rgba(255, 152, 0, 0.35)"
                  stroke="#ff9800"
                  strokeWidth="3.5"
                  strokeLinejoin="round"
                  style={{ filter: "drop-shadow(0 0 5px rgba(255, 152, 0, 0.6))" }}
                />
              )}

              {/* Highly visible solid connecting lines between placed points */}
              {points.length >= 2 &&
                points.map((pt, idx) => {
                  if (idx === 0) return null;
                  const prev = points[idx - 1];
                  return (
                    <line
                      key={`line-${idx}`}
                      x1={`${prev.x}%`}
                      y1={`${prev.y}%`}
                      x2={`${pt.x}%`}
                      y2={`${pt.y}%`}
                      stroke="#ff9800"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ filter: "drop-shadow(0 0 4px rgba(255, 152, 0, 0.7))" }}
                    />
                  );
                })}

              {/* Closing line preview (dashed) back to vertex 1 while drawing */}
              {!isPolygonComplete && points.length >= 3 && (
                <line
                  x1={`${points[points.length - 1].x}%`}
                  y1={`${points[points.length - 1].y}%`}
                  x2={`${points[0].x}%`}
                  y2={`${points[0].y}%`}
                  stroke="#ff9800"
                  strokeWidth="2.5"
                  strokeDasharray="6 4"
                  strokeOpacity="0.6"
                />
              )}

              {/* Vertex circles + labels with high contrast */}
              {points.map((pt, idx) => (
                <g key={idx}>
                  <circle
                    cx={`${pt.x}%`}
                    cy={`${pt.y}%`}
                    r="9"
                    fill={idx === 0 ? "#e91e63" : "#ff9800"}
                    stroke="#ffffff"
                    strokeWidth="2.5"
                    style={{
                      cursor: "grab",
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.8))",
                    }}
                    onMouseDown={(e) => handlePointMouseDown(idx, e)}
                  />
                  <text
                    x={`${pt.x}%`}
                    y={`${pt.y}%`}
                    fill="#ffffff"
                    fontSize="11"
                    fontWeight="900"
                    textAnchor="middle"
                    dominantBaseline="central"
                    pointerEvents="none"
                    style={{ userSelect: "none" }}
                  >
                    {idx + 1}
                  </text>
                </g>
              ))}
            </svg>
          </Paper>

          <Typography
            variant="caption"
            color="text.disabled"
            display="block"
            textAlign="center"
            mt={1}
          >
            Reference coordinate space: {refWidth} × {refHeight} px
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
              <Tooltip title="Remove the last placed point">
                <span>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<UndoIcon />}
                    onClick={handleUndo}
                    disabled={points.length === 0}
                    size="small"
                  >
                    Undo Point
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Remove all points and start over">
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleClear}
                    disabled={points.length === 0}
                    size="small"
                  >
                    Clear All
                  </Button>
                </span>
              </Tooltip>

              {savedPoints.length > 0 && (
                <Tooltip title="Restore the last saved polygon">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<RestoreIcon />}
                      onClick={handleResetToSaved}
                      disabled={!hasUnsavedChanges}
                      size="small"
                    >
                      Reset to Saved
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>

            <Tooltip
              title={
                !selectedCameraId
                  ? "Select a camera first"
                  : !isSaveValid
                  ? `Complete the polygon (${points.length}/${targetPointCount} points)`
                  : saving
                  ? "Saving…"
                  : "Save the polygon to the server"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSave}
                  disabled={!canSave}
                >
                  {saving ? "Saving…" : "Save Area of Interest"}
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {hasUnsavedChanges && points.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }} icon={false}>
              <Typography variant="caption">
                You have unsaved changes.{" "}
                {!isSaveValid &&
                  `(${points.length}/${targetPointCount} points placed)`}
              </Typography>
            </Alert>
          )}
        </Box>
      )}
    </Card>
  );
};

export default ExitBlockingPolygonEditor;
